import { useState, useEffect, useRef } from 'react';
import { MagnifyingGlass, X, Flask, MapPin, ArrowSquareOut, SpinnerGap, UserCircle } from '@phosphor-icons/react';
import AppNav from '../components/AppNav';
import AppFooter from '../components/AppFooter';
import PageMeta from '../components/PageMeta';
import JsonLd, { softwareAppSchema } from '../components/JsonLd';
import { useAuth } from '../contexts/AuthContext';
import { searchTrials, fetchTrialMatches } from '../utils/trials';
import type { ClinicalTrial, TrialMatch } from '@researcher/shared';
import { reportError } from '../utils/errorReporting';

export default function TrialMatcher() {
  const { currentUser } = useAuth();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ClinicalTrial[]>([]);
  const [matches, setMatches] = useState<TrialMatch[]>([]);
  const [featuredTrials, setFeaturedTrials] = useState<ClinicalTrial[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesDone, setMatchesDone] = useState(false);
  const [searchError, setSearchError] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Fetch personalized matches for authenticated users
  useEffect(() => {
    if (!currentUser) { setMatchesDone(true); return; }
    setMatchesLoading(true);
    fetchTrialMatches()
      .then(setMatches)
      .catch(err => console.error('Error fetching matches:', err))
      .finally(() => { setMatchesLoading(false); setMatchesDone(true); });
  }, [currentUser]);

  // When matches come back empty (or user not logged in), load featured trials
  useEffect(() => {
    if (!matchesDone || matches.length > 0) return;
    searchTrials("women's health")
      .then(trials => setFeaturedTrials(trials.slice(0, 5)))
      .catch(err => console.error('Error fetching featured trials:', err));
  }, [matchesDone, matches.length]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim() || query.trim().length < 2) {
      setSearchResults([]);
      setSearchError('');
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      setSearchError('');
      try {
        const results = await searchTrials(query.trim());
        setSearchResults(results);
      } catch (err: any) {
        setSearchError(reportError(err, 'search'));
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
      <PageMeta
        title="Clinical Trial Finder for Women's Health"
        description="Find clinical trials for endometriosis, PCOS, PMDD, fibroids, and other women's health conditions. Personalized trial matching powered by ClinicalTrials.gov."
        path="/trials"
      />
      <JsonLd data={softwareAppSchema({ name: 'Clinical Trial Finder', description: 'Find and match clinical trials for women\'s health conditions including endometriosis, PCOS, PMDD, fibroids, and more. Powered by ClinicalTrials.gov.', url: 'https://romyhealth.co/trials' })} />
      <AppNav />
      <main className="pt-16 pb-16 md:pb-0 min-h-screen">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="flex justify-center mb-4">
              <Flask size={64} className="text-orange-500" weight="regular" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="text-gray-800 dark:text-gray-100">Clinical Trial</span>{' '}
              <span className="text-pink-500">Finder</span>
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Search for recruiting clinical trials or get personalized matches based on your health profile.
            </p>
          </div>

          {/* Search bar */}
          <div className="max-w-2xl mx-auto mb-10">
            <div className="relative">
              <MagnifyingGlass
                size={20}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-pink-500"
              />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search by condition (e.g., endometriosis, PCOS, PMDD)..."
                className="w-full pl-12 pr-10 py-3.5 bg-white dark:bg-gray-800 border-2 border-pink-200 dark:border-pink-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 shadow-sm text-lg"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={18} weight="bold" />
                </button>
              )}
            </div>
            {searchLoading && (
              <div className="flex items-center gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
                <SpinnerGap size={14} className="animate-spin" />
                Searching ClinicalTrials.gov...
              </div>
            )}
            {searchError && (
              <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">{searchError}</p>
            )}
          </div>

          {/* Search results */}
          {searchResults.length > 0 && (
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
                Search Results
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                  ({searchResults.length} trials)
                </span>
              </h2>
              <div className="space-y-4">
                {searchResults.map(trial => (
                  <TrialCard key={trial.nctId} trial={trial} />
                ))}
              </div>
            </section>
          )}

          {/* Personalized matches */}
          {currentUser && matches.length > 0 && (
            <section className="mb-12">
              <div className="flex items-center gap-2 mb-4">
                <UserCircle size={24} className="text-pink-500" />
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Your Matches</h2>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Personalized matches based on your health profile conditions.
              </p>
              <div className="space-y-4">
                {matches.map(match => (
                  <TrialCard
                    key={match.trial.nctId}
                    trial={match.trial}
                    matchScore={match.matchScore}
                    matchReasons={match.matchReasons}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Loading state for matches */}
          {currentUser && matchesLoading && (
            <div className="text-center py-8 mb-12">
              <SpinnerGap size={24} className="animate-spin text-pink-500 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Finding matches...</p>
            </div>
          )}

          {/* Featured trials — shown when no personalized matches */}
          {!matchesLoading && matches.length === 0 && featuredTrials.length > 0 && !query && (
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                Featured Trials
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Recruiting studies in women's health.
                {currentUser
                  ? ' Add conditions to your health profile for personalized matches.'
                  : ' Sign in and complete your health profile for personalized matches.'}
              </p>
              <div className="space-y-4">
                {featuredTrials.map(trial => (
                  <TrialCard key={trial.nctId} trial={trial} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <AppFooter />
    </div>
  );
}

function TrialCard({
  trial,
  matchScore,
  matchReasons,
}: {
  trial: ClinicalTrial;
  matchScore?: number;
  matchReasons?: string[];
}) {
  const statusColor = trial.status === 'RECRUITING'
    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
              {trial.status}
            </span>
            {trial.phase !== 'N/A' && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                {trial.phase}
              </span>
            )}
            {matchScore !== undefined && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400">
                {Math.round(matchScore * 100)}% match
              </span>
            )}
          </div>
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 leading-tight">{trial.title}</h3>
        </div>
        <a
          href={trial.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 p-2 text-pink-500 hover:text-pink-600 hover:bg-pink-50 dark:hover:bg-pink-950 rounded-lg transition-colors"
          title="View on ClinicalTrials.gov"
        >
          <ArrowSquareOut size={18} />
        </a>
      </div>

      {trial.briefSummary && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">{trial.briefSummary}</p>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
        {trial.sponsor && <span>Sponsor: {trial.sponsor}</span>}
        {trial.enrollmentCount > 0 && <span>Enrollment: {trial.enrollmentCount}</span>}
        {trial.conditions.length > 0 && (
          <span>Conditions: {trial.conditions.slice(0, 3).join(', ')}</span>
        )}
      </div>

      {trial.locations.length > 0 && (
        <div className="mt-2 flex items-start gap-1 text-xs text-gray-500 dark:text-gray-400">
          <MapPin size={12} className="mt-0.5 flex-shrink-0" />
          <span>
            {trial.locations
              .slice(0, 3)
              .map(l => [l.city, l.state, l.country].filter(Boolean).join(', '))
              .join(' | ')}
            {trial.locations.length > 3 && ` +${trial.locations.length - 3} more`}
          </span>
        </div>
      )}

      {matchReasons && matchReasons.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {matchReasons.map((reason, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 rounded">
              {reason}
            </span>
          ))}
        </div>
      )}

      <div className="mt-2 text-[10px] text-gray-400 dark:text-gray-500">{trial.nctId}</div>
    </div>
  );
}
